// © Microsoft Corporation. All rights reserved.

import {PublisherProfileTabsTelemetryObjects, publisherProfileTabsTelemetryActionDispatchers, publisherProfileTabsTelemetryContracts} from "./PublisherProfileTabs.telemetry";
import { addOrUpdateTelemetryObject, renderHybrid } from "../../Social.telemetry";
import { publisherTabAll, publisherTabPremium } from "../../utils/icons";

import React from "react";
import { SocialProps } from "../../Social.props";
import SortBox from "../../common/sort-box/SortBox";
import { classNames } from "@microsoft/fast-web-utilities";
import { getContextData } from "@msnews/core";

interface PublisherProfileTabsState {
    activeTab: Object;
    sortType: string;
    contentCards: Array<React.Component>;
}
export default class PublisherProfileTabs extends React.Component<SocialProps, PublisherProfileTabsState> {
    telemetryObjects: PublisherProfileTabsTelemetryObjects;
    contentContainerRef: React.RefObject<HTMLDivElement>;
    isLoadingNewContent: boolean;

    private static tabs = {
        All: {label: "All", icon: publisherTabAll},
        Premium: {label: "Premium", icon: publisherTabPremium}
    };
    static sortTypes = {
        Recommend: "PublisherProfilePageTabSortByRecommend",
        Latest: "PublisherProfilePageTabSortByLatest",
        MostPopular: "PublisherProfilePageTabSortByMostPopular"
    };

    constructor(props) {
        super(props);
        this.contentContainerRef = React.createRef();

        this.state = {
            activeTab: null,
            sortType: "Recommend",
            contentCards: []
        };
        this.telemetryObjects = null;

        PublisherProfileTabs.tabs = {
            All: {label: props.t.PublisherProfilePageTabsAllTab, icon: publisherTabAll},
            Premium: {label: props.t.PublisherProfilePageTabsPremiumTab, icon: publisherTabPremium}
        };
    }

    componentDidMount() {
        window.addEventListener("scroll", this.handleScroll.bind(this));
        const { requestContext } = this.props;

        const locationHref = getContextData(requestContext.data.locationHref, "locationHref");
        const tabNameFromUrl = Object.keys(PublisherProfileTabs.tabs).filter(key => locationHref.toLowerCase().endsWith(key.toLowerCase())).pop();
        if (tabNameFromUrl) {
            this.setState({ activeTab: PublisherProfileTabs.tabs[tabNameFromUrl] });
        }

        if (this.props.componentConnector && this.props.componentConnector.getContentCards) {
            this.loadMoreContent(tabNameFromUrl === "premium");
        }
    }

    componentWillUnmount() {
        window.removeEventListener("scroll", this.handleScroll.bind(this));
    }

    componentDidUpdate(prevProps) {
        /* when opened a new publisher profile without rerendering component itself, reset the active tab */
        if (prevProps.id != this.props.id) {
            this.showTab(null, null, null);
        }
    }

    loadMoreContent(premium?: boolean, contentCards?) {
        const { id, market } = this.props;
        const currentContentCards = contentCards ? contentCards : this.state.contentCards;
        let cardsPromise;
        const args = {
            top: 15,
            skip: currentContentCards.length
        };
        if (premium) {
            cardsPromise = this.props.componentConnector.getContentCards(id, market, true, args);
        } else {
            cardsPromise = this.props.componentConnector.getContentCards(id, market, false, args);
        }
        return cardsPromise.then(cards => {
            const existingCardKeys = {};
            const newContentCards = [];
            [...currentContentCards, ...cards].forEach(card => {
                if (!existingCardKeys[card.key]) {
                    newContentCards.push(card);
                    existingCardKeys[card.key] = true;
                }
            });
            this.setState(() => ({ contentCards: newContentCards }));
        });
    }

    handleScroll() {
        const elem = this.contentContainerRef.current;
        if (!this.props.id || !elem || this.isLoadingNewContent) {
            return;
        }
        const scrollPosition = elem.offsetHeight - window.pageYOffset - window.innerHeight;
        if (scrollPosition < 100) {
            this.isLoadingNewContent = true;
            this.loadMoreContent(this.state.activeTab == PublisherProfileTabs.tabs.Premium)
                .then(() => { this.isLoadingNewContent = false; });
        }
    }

    showTab(e, tab, telemetryObject) {
        if (this.state.activeTab == tab) { return; }

        this.setState({
            activeTab: tab,
            contentCards: []
        });
        this.loadMoreContent(tab == PublisherProfileTabs.tabs.Premium, []);

        if (e) {
            document.getElementById("tabHeaders").scrollLeft = document.getElementById(tab.label).offsetLeft;
            telemetryObject && publisherProfileTabsTelemetryActionDispatchers.tabClicked(e.currentTarget, telemetryObject);
        }
    }

    onSortTypeChange(newSortType) {
        this.setState({ sortType: newSortType });
    }

    render() {
        const { managedClasses, responsiveClass, children, mainTelemetryObject } = this.props;
        const activeTab = this.state.activeTab || PublisherProfileTabs.tabs.All;

        this.telemetryObjects = {
            mainItem: addOrUpdateTelemetryObject(mainTelemetryObject, publisherProfileTabsTelemetryContracts.mainItem, {}, this.telemetryObjects && this.telemetryObjects.mainItem),
            tabs: {}
        };

        return (
            <div role="main" className={classNames(
                "cwt-publisher-profile-tabs-container",
                managedClasses.publisherProfileTabsContainer,
                responsiveClass
            )}>
                <ul id="tabHeaders" className={"cwt-tabs cwt-publisher-profile-tabs no-select"} role="tablist">
                    { Object.keys(PublisherProfileTabs.tabs).map(key => {
                        const tab = PublisherProfileTabs.tabs[key];
                        this.telemetryObjects.tabs[key] = addOrUpdateTelemetryObject(mainTelemetryObject, publisherProfileTabsTelemetryContracts.tabs[key], {}, this.telemetryObjects && this.telemetryObjects.tabs[key]);
                        return (
                            <li role="tab" id={tab.label} key={tab.label} className={`${activeTab == tab && "active"} cwt-tab-item cwt-link`} onClick={(e) => this.showTab(e, tab, this.telemetryObjects.tabs[key])}
                                aria-selected={activeTab == tab}
                                {...renderHybrid(this.telemetryObjects.tabs[key], false)}
                            >
                                <i className="cwt-icon">
                                    {tab.icon}
                                    <span className="cwt-icon-label">{tab.label}</span>
                                </i>
                            </li>
                        );
                    })}
                </ul>
                {this.renderSortBox()}
                <div className="cwt-tab-content" ref={this.contentContainerRef}>
                    {...this.state.contentCards}
                    {children["cardAction"]}
                </div>
            </div>
        );
    }

    renderSortBox() {
        const { mainTelemetryObject, t, id, managedClasses } = this.props;
        return (
            <div className="cwt-sortbox-container">
                <SortBox selectedSortType={this.state.sortType} sortTypes={PublisherProfileTabs.sortTypes} dontShowLabel={true}
                    {...{mainTelemetryObject: this.telemetryObjects.mainItem, t, managedClasses, id}}
                    onChange={this.onSortTypeChange.bind(this)}
                />
            </div>
        );
    }
}